import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, HandCoins } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { dayLabel, downloadCSV, METHOD_LABEL, RECOVERY_LABEL, todayISO } from '@/lib/hr'
import type { HRAdvance } from '@/types/hr'
import { Empty, Segmented, errMsg } from './shared'
import { AdvanceDialog, ReasonDialog, invalidateHRMoney } from './MoneyDialogs'
import { useHREmployees } from './PeopleTab'
import { hrRole } from './HRPage'

// Advances (F-HR-N4): who owes what, and how it's coming back. Payroll
// suggests each month's recovery; it never takes more than the month's pay.

export function AdvancesTab() {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const { isAdmin } = hrRole()
  const [status, setStatus] = useState<'open' | 'all'>('open')
  const [giving, setGiving] = useState(false)
  const [writeOff, setWriteOff] = useState<HRAdvance | null>(null)
  const { people } = useHREmployees('active')
  const q = useQuery({ queryKey: ['hr', 'advances', status], queryFn: () => apiClient.hrListAdvances(status) })
  const advances = (q.data?.data as HRAdvance[] | undefined) ?? []
  const total = advances.reduce((s, a) => s + a.outstanding, 0)

  const wo = useMutation({
    mutationFn: (v: { id: string; note: string }) => apiClient.hrWriteOffAdvance(v.id, v.note),
    onSuccess: () => {
      toastHelpers.success('Written off')
      invalidateHRMoney(qc)
      setWriteOff(null)
    },
    onError: (err) => toastHelpers.error('Couldn’t write off', errMsg(err)),
  })

  const exportCSV = () =>
    downloadCSV(`advances-${todayISO()}.csv`, [
      ['Name', 'Given on', 'Amount', 'Recovered', 'Written off', 'Outstanding', 'Paid back', 'Reason', 'Paid by', 'Reference'],
      ...advances.map((a) => [
        a.full_name,
        a.given_on,
        a.amount,
        a.recovered,
        a.written_off,
        a.outstanding,
        RECOVERY_LABEL[a.recovery_plan] + (a.installments ? ` (${a.installments})` : '') + (a.monthly_amount ? ` (${a.monthly_amount}/month)` : ''),
        a.reason ?? '',
        a.method ? METHOD_LABEL[a.method] : a.is_opening ? 'Opening balance' : '',
        a.reference ?? '',
      ]),
    ])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { id: 'open', label: 'Still owed' },
              { id: 'all', label: 'All' },
            ]}
          />
          {status === 'open' && advances.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {formatCurrency(total)} owed by {new Set(advances.map((a) => a.employee_id)).size} people
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={!advances.length}>
            <Download className="mr-1.5 h-4 w-4" />
            Export
          </Button>
          <Button size="sm" onClick={() => setGiving(true)}>
            <HandCoins className="mr-1.5 h-4 w-4" />
            Give advance
          </Button>
        </div>
      </div>

      {q.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : advances.length === 0 ? (
        <Empty>{status === 'open' ? 'Nobody owes an advance.' : 'No advances yet.'}</Empty>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Given</th>
                <th className="px-4 py-2 text-right font-medium">Amount</th>
                <th className="px-4 py-2 text-right font-medium">Recovered</th>
                <th className="px-4 py-2 text-right font-medium">Still owed</th>
                <th className="px-4 py-2 font-medium">Paid back</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {advances.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{a.full_name}</div>
                    {a.reason && <div className="text-xs text-muted-foreground">{a.reason}</div>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">
                    {dayLabel(a.given_on, true)}
                    <div className="text-xs">{a.is_opening ? 'Opening balance' : a.method ? METHOD_LABEL[a.method] : ''}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(a.amount)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {formatCurrency(a.recovered)}
                    {a.written_off > 0 && <div className="text-xs text-muted-foreground">{formatCurrency(a.written_off)} written off</div>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium tabular-nums">{formatCurrency(a.outstanding)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {RECOVERY_LABEL[a.recovery_plan]}
                    {a.installments ? ` · ${a.installments} months` : ''}
                    {a.monthly_amount ? ` · ${formatCurrency(a.monthly_amount)}/month` : ''}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {isAdmin && a.outstanding > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => setWriteOff(a)}>
                        Write off
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdvanceDialog open={giving} onClose={() => setGiving(false)} people={people} />
      <ReasonDialog
        open={!!writeOff}
        onClose={() => setWriteOff(null)}
        title={`Write off ${writeOff ? formatCurrency(writeOff.outstanding) : ''}?`}
        description={`${writeOff?.full_name} will no longer owe it, and payroll stops recovering it. This can’t be undone.`}
        action="Write off"
        destructive
        busy={wo.isPending}
        onConfirm={(note) => writeOff && wo.mutate({ id: writeOff.id, note })}
      />
    </div>
  )
}
